import { NextPageContext } from "next";

import { StaticApiRouteSpec } from "@every.org/common/src/routes";
import { getDonationByShortIdRouteSpec } from "@every.org/common/src/routes/donation";

import { ViewDonationData } from "src/context/ViewDonationContext";
import { addCacheControl } from "src/utilities/ssr/caching";
import { validateAndDecodeParam } from "src/utilities/ssr/validateAndDecodeParam";
import { serversideStaticQueryApi } from "src/utility/apiClient";

export type UserDonationsServerSideProps = {
  nonprofitSlug: string;
  shortId: number;
  username: string;
  notFound?: boolean;
} & Partial<Awaited<ReturnType<typeof getUserDonationsByShortId>>>;

const getUserDonationsByShortId = async ({
  nonprofitSlug,
  username,
  shortId,
  ctx,
}: {
  nonprofitSlug: string;
  shortId: string;
  username: string;
  ctx: NextPageContext;
}) => {
  const donationResult: ViewDonationData = await serversideStaticQueryApi(
    getDonationByShortIdRouteSpec as StaticApiRouteSpec,
    {
      routeTokens: {
        nonprofitSlug,
        shortId,
        username,
      },
      queryParams: {},
    },
    ctx
  );

  return {
    initialData: {
      nonprofits: [donationResult.nonprofit],
      users: [donationResult.user],
      tags: donationResult.nonprofitTags,
    },
    donation: donationResult,
  };
};

export const getUserDonationServerSideProps = async (
  ctx: NextPageContext
): Promise<{ props: UserDonationsServerSideProps }> => {
  const nonprofitSlug = validateAndDecodeParam(ctx, "nonprofitSlug");
  const username = validateAndDecodeParam(ctx, "username");
  const shortId = getShortId(ctx);

  const props = { nonprofitSlug, shortId, username };

  // Not on server, do nothing - let client-side data fetching handle this
  if (!ctx.res) {
    return { props };
  }

  // Cache this SSR response at edge
  addCacheControl(ctx);

  try {
    const data = await getUserDonationsByShortId({
      nonprofitSlug,
      shortId: shortId.toString(),
      username,
      ctx,
    });

    const initialData = {
      ...data.initialData,
    };

    return { props: { ...props, ...data, initialData } };
  } catch (error) {
    ctx.res.statusCode = 404;
    return { props: { ...props, notFound: true } };
  }
};

function getShortId(ctx: NextPageContext): number {
  let shortId: number;

  /**
   * to avoid the "getServerSideProps is missing expected param" error
   * validate and decode shortId only if presents
   *
   * ctx.query["shortId"] will not be undefined on /:@username:/:nonprofitSlug:/:shortId:/
   * and will be undefined on /:@username:/:nonprofitSlug:/
   */
  if (ctx.query["shortId"]) {
    try {
      shortId = parseInt(validateAndDecodeParam(ctx, "shortId"), 10);
    } catch (e) {
      shortId = 1;
    }
  } else {
    shortId = 1;
  }

  return shortId;
}
